import { Alert } from '@mui/material'
import { FC, useMemo } from 'react'
import type { RaRecord } from 'react-admin'
import { Show, useRecordContext, useShowContext, useTranslate } from 'react-admin'

import { formatDay } from '../../domain/activeInspectionCells'
import type { ActiveInspection } from '../../models/activeInspection'
import { ACTIVE_INSPECTION_COLUMNS, activeInspectionRowSchema, convertToActiveInspection } from '../../models/activeInspection'
import { LoadingDisplay } from '../atoms/LoadingDisplay'
import { InspectionDetails } from '../pages/InspectionDetails'

/**
 * The raw row, read through the same schema and conversion the Operativa table
 * runs on every row. A row that fails either — drift between the app and the
 * database, or a status with no lifecycle meaning — is reported rather than
 * rendered wearing a state nothing assigned it, exactly as the table skips it.
 */
const interpret = (record: RaRecord): ActiveInspection | null => {
  const parsed = activeInspectionRowSchema.safeParse(record)

  if (!parsed.success) {
    return null
  }

  try {
    return convertToActiveInspection(parsed.data)
  } catch {
    return null
  }
}

const ShowRecord: FC = () => {
  const { record } = useShowContext<RaRecord>()
  const translate = useTranslate()
  const inspection = useMemo(() => (record ? interpret(record) : null), [record])

  if (!record) return <LoadingDisplay />

  if (!inspection) {
    return <Alert severity="error">{translate('resources.inspections.load_error')}</Alert>
  }

  return <InspectionDetails inspection={inspection} />
}

/** Date, then container — the title the archived details screen carries. */
export const InspectionShowTitle: FC = () => {
  const record = useRecordContext()

  if (!record) return null

  const day = typeof record.day === 'string' ? record.day : null
  const container = typeof record.container === 'string' ? record.container : ''
  const date = formatDay(day)

  return (
    <span>
      {date} {container}
    </span>
  )
}

/**
 * The show view of the live `inspections` resource.
 *
 * Reads the row with the child evidence tables embedded — the same columns the
 * Operativa table requests — so the conversion the table runs applies unchanged.
 * An id the live table no longer holds fails the read, and React Admin's own
 * handling takes over: a not-found notice and a return to the list.
 */
export const InspectionShow: FC = () => (
  <Show title={<InspectionShowTitle />} queryOptions={{ meta: { columns: [...ACTIVE_INSPECTION_COLUMNS] } }}>
    <ShowRecord />
  </Show>
)
